import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useBranding } from '../../contexts/BrandingContext';
import notificationService from '../../services/notificationService';
import { Notification as NotificationType } from '../../types/notification';
import { notificationDir, notificationMessage, notificationTitle } from '../../utils/notificationText';
import { relativeTime } from '../../utils/relativeTime';
import Dropdown, { DropdownItem } from '../ui/Dropdown';
import Avatar from '../ui/Avatar';

interface HeaderProps {
  onSidebarToggle: () => void;
  sidebarCollapsed?: boolean;
}

const Header: React.FC<HeaderProps> = ({ onSidebarToggle, sidebarCollapsed = false }) => {
  const { user, logout } = useAuth();
  const { language, setLanguage, isRTL, t } = useLanguage();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [recentNotifications, setRecentNotifications] = useState<NotificationType[]>([]);

  const systemTitle = language === 'ar'
    ? (branding.system_title_ar || branding.system_title)
    : (branding.system_title_en || branding.system_title);

  const handleLanguageChange = (lang: string) => {
    setLanguage(lang);
  };

  const handleLogout = () => {
    logout();
  };

  const loadNotifications = async () => {
    try {
      const [count, notifications] = await Promise.all([
        notificationService.getUnreadCount(),
        notificationService.getNotifications()
      ]);
      setUnreadCount(count);
      setRecentNotifications(notifications.slice(0, 5));
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setUnreadCount(0);
      setRecentNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };


  const userName = user?.full_name ||
    (user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : null) ||
    user?.email ||
    'User';

  return (
    <header className={classNames('ecme-header', { 'sidebar-collapsed': sidebarCollapsed })}>
      <div className="ecme-header-wrapper">
        {/* Left Section */}
        <div className="flex items-center gap-3">
          {/* Mobile menu button */}
          <button
            className="ecme-header-action-item lg:hidden"
            onClick={onSidebarToggle}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          {/* Mobile logo */}
          <div className="ecme-header-logo lg:hidden">
            <img src={branding.logo_url} alt={branding.company_name} className="h-8" />
            <span className="ecme-header-logo-text">{systemTitle}</span>
          </div>
        </div>

        {/* Center Section - System Title (Desktop) */}
        <div className="hidden lg:block">
          <h5 className="text-lg font-semibold text-white text-center">{systemTitle}</h5>
        </div>

        {/* Right Section */}
        <div className="ecme-header-action">
          {/* Search (Desktop) — Enter jumps to the transactions list pre-filtered */}
          <div className="hidden md:block relative">
            <svg className="w-5 h-5 absolute start-3 top-1/2 -translate-y-1/2 text-white/50 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              className="ecme-header-search-input"
              placeholder={t('searchTransactions')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && searchQuery.trim()) {
                  navigate(`/transactions?search=${encodeURIComponent(searchQuery.trim())}`);
                }
              }}
            />
          </div>

          {/* Notifications */}
          <Dropdown
            placement="bottom-end"
            renderTitle={
              <span className="ecme-header-action-item relative cursor-pointer">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {unreadCount > 0 && (
                  <span className="ecme-header-notification-badge animate-pulse">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </span>
            }
            menuClassName="w-80"
          >
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-900">{t('notifications')}</span>
                <button
                  className="text-primary text-sm hover:underline"
                  onClick={handleMarkAllAsRead}
                >
                  {t('markAllAsRead')}
                </button>
              </div>
            </div>
            <div className="max-h-[300px] overflow-y-auto">
              {recentNotifications.length > 0 ? (
                recentNotifications.map(notification => (
                  <Link
                    key={notification.id}
                    to="/notifications"
                    className={classNames(
                      'flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors',
                      { 'bg-primary-subtle': !notification.is_read }
                    )}
                  >
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      {/* An explicit base direction makes the ellipsis fall at the text's own end */}
                      <p className="text-sm font-semibold text-gray-900 truncate" dir={notificationDir(notification.title_ar, isRTL)}>{notificationTitle(notification, isRTL)}</p>
                      <p className="text-sm text-gray-500 truncate" dir={notificationDir(notification.message_ar, isRTL)}>{notificationMessage(notification, isRTL)}</p>
                      <p className="text-xs text-gray-400 mt-1">{relativeTime(notification.created_at, isRTL)}</p>
                    </div>
                    {!notification.is_read && (
                      <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-2" />
                    )}
                  </Link>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <svg className="w-12 h-12 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  <p>{t('noNotifications')}</p>
                </div>
              )}
            </div>
            <div className="px-4 py-3 border-t border-gray-200 text-center">
              <Link to="/notifications" className="text-primary text-sm font-medium hover:underline">
                {t('viewAllNotifications')}
              </Link>
            </div>
          </Dropdown>

          {/* Divider */}
          <div className="hidden md:block w-px h-6 bg-white/20" aria-hidden="true" />

          {/* Language Toggle — segmented pill */}
          <div className="flex items-center bg-white/10 rounded-full p-0.5">
            <button
              className={classNames(
                'ecme-header-lang-segment',
                { active: language === 'en' }
              )}
              onClick={() => handleLanguageChange('en')}
            >
              EN
            </button>
            <button
              className={classNames(
                'ecme-header-lang-segment',
                { active: language === 'ar' }
              )}
              onClick={() => handleLanguageChange('ar')}
            >
              AR
            </button>
          </div>

          {/* User Menu */}
          <Dropdown
            placement="bottom-end"
            renderTitle={
              <div className="ecme-header-user-dropdown">
                <Avatar size="sm" name={userName} />
                <div className="ecme-header-user-name">{userName}</div>
                <svg className="w-4 h-4 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            }
          >
            <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-3">
              <Avatar size="sm" name={userName} />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate mb-0">{userName}</p>
                <p className="text-xs text-gray-500 mb-0">
                  {user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'Role'}
                </p>
              </div>
            </div>
            <DropdownItem onClick={() => navigate('/profile')}>
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span>{t('profile')}</span>
            </DropdownItem>
            <div className="ecme-dropdown-divider" />
            <DropdownItem className="ecme-dropdown-item-danger" onClick={handleLogout}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span>{t('logout')}</span>
            </DropdownItem>
          </Dropdown>
        </div>
      </div>
    </header>
  );
};

export default Header;
